import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { ArrowLeft, Building2, GraduationCap, Users, UserCircle, Loader2, Download, Smartphone, QrCode, Eye, EyeOff, Moon, Sun } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const portals = [
  {
    id: 'admin',
    title: 'Admin Portal',
    description: 'School Administrators',
    icon: Building2,
    color: 'bg-blue-500',
    roles: ['school_admin', 'staff']
  },
  {
    id: 'staff',
    title: 'Staff Portal',
    description: 'Teachers & Staff',
    icon: GraduationCap,
    color: 'bg-green-500',
    roles: ['teacher', 'staff']
  },
  {
    id: 'student',
    title: 'Student Portal',
    description: 'Students',
    icon: Users,
    color: 'bg-purple-500',
    roles: ['student']
  },
  {
    id: 'parent',
    title: 'Parent Portal',
    description: 'Parents & Guardians',
    icon: UserCircle,
    color: 'bg-amber-500',
    roles: ['parent']
  }
];

const TenantLoginPage = () => {
  const { schoolCode } = useParams();
  const { theme, toggleTheme } = useTheme();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState(null);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Install App states
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSchool();
    
    // Save school_code and login_source to localStorage for proper logout redirect
    if (schoolCode) {
      localStorage.setItem('school_code', schoolCode);
      localStorage.setItem('login_source', `/${schoolCode}`);
    }
    
    // Detect iOS
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);
    
    // Listen for PWA install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [schoolCode]);

  useEffect(() => {
    if (user && school) {
      navigateToDashboard(user.role);
    }
  }, [user, school]);

  const fetchSchool = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schools/by-code/${schoolCode}`);
      setSchool(response.data);
      setLoading(false);
    } catch (error) {
      console.error('School not found:', error);
      setNotFound(true);
      setLoading(false);
    }
  };

  const navigateToDashboard = (role) => {
    switch (role) {
      case 'school_admin':
        navigate('/admin');
        break;
      case 'teacher':
        navigate('/teacher');
        break;
      case 'student':
        navigate('/student');
        break;
      case 'parent':
        navigate('/parent');
        break;
      case 'staff':
        navigate('/staff-dashboard');
        break;
      default:
        navigate('/');
    }
  };

  // Get app type from portal id
  const getAppType = (portalId) => {
    const mapping = {
      'staff': 'teacher',
      'student': 'student',
      'parent': 'parent'
    };
    return mapping[portalId] || portalId;
  };

  // Fetch QR code for the selected portal
  const fetchQRCode = async (portalId) => {
    if (!school?.id) return;
    try {
      const appType = getAppType(portalId);
      const response = await axios.get(
        `${API_URL}/api/pwa/qr/${school.id}/${appType}?format=base64`
      );
      setQrCode(response.data.qr_code);
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
    }
  };

  // Handle install app button click
  const handleInstallApp = async () => {
    if (deferredPrompt) {
      // Chrome/Android - use native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
        // Track installation
        try {
          await axios.post(`${API_URL}/api/pwa/track-install`, {
            school_id: school?.id,
            app_type: getAppType(selectedPortal?.id),
            device_info: { userAgent: navigator.userAgent }
          });
        } catch (e) { /* ignore */ }
      }
      setDeferredPrompt(null);
    } else {
      // Show dialog with QR code and instructions
      await fetchQRCode(selectedPortal?.id);
      setShowInstallDialog(true);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoginLoading(true);
    try {
      // Pass school_code and expected_role for server-side validation
      const userData = await login(email, password, schoolCode, selectedPortal.id);
      
      // Save school_code for logout redirect
      localStorage.setItem('school_code', schoolCode);
      
      navigateToDashboard(userData.role);
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoginLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-slate-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="bg-red-100 dark:bg-red-900/30 p-6 rounded-full inline-block mb-4">
            <Building2 className="h-12 w-12 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">School Not Found</h1>
          <p className="text-muted-foreground mb-6">
            No school found with code: <span className="font-mono font-bold">{schoolCode.toUpperCase()}</span>
          </p>
          <Link to="/auth">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Portal Selection
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  // If no portal selected, show portal selection (same as /auth design)
  if (!selectedPortal) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-slate-900 p-4 relative">
        {/* Theme Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-4xl"
        >
          <div className="text-center mb-10">
            {school?.logo_url ? (
              <div className="w-20 h-20 mx-auto mb-4 rounded-full border-4 border-primary/30 overflow-hidden bg-white flex items-center justify-center p-1.5">
                <img 
                  src={school.logo_url} 
                  alt={school.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div className="bg-primary/10 p-4 rounded-full inline-block mb-4">
                <Building2 className="h-10 w-10 text-primary" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-foreground">{school?.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Code: <span className="font-mono font-semibold">{schoolCode.toUpperCase()}</span>
              {school?.current_session && ` • Session: ${school.current_session}`}
            </p>
            <h2 className="text-xl font-semibold mt-6 text-foreground">Select Your Portal</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {portals.map((portal, index) => {
              const Icon = portal.icon;
              return (
                <motion.div
                  key={portal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="h-full"
                >
                  <Card 
                    className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105 border-2 hover:border-primary/50 h-full flex flex-col"
                    onClick={() => setSelectedPortal(portal)}
                    data-testid={`portal-${portal.id}`}
                  >
                    <CardHeader className="text-center flex-1 flex flex-col items-center justify-center py-6">
                      <div className={`${portal.color} w-14 h-14 rounded-full flex items-center justify-center mb-3`}>
                        <Icon className="h-7 w-7 text-white" strokeWidth={1.5} />
                      </div>
                      <CardTitle className="text-base">{portal.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center pt-0 pb-4">
                      <p className="text-sm text-muted-foreground">{portal.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <footer className="mt-10 text-center space-y-3">
          <div className="flex justify-center gap-4 text-xs text-muted-foreground">
            <Link to={`/legal/${schoolCode}/privacy-policy`} className="hover:text-primary hover:underline">
              Privacy Policy
            </Link>
            <span>•</span>
            <Link to={`/legal/${schoolCode}/terms-of-use`} className="hover:text-primary hover:underline">
              Terms of Use
            </Link>
            <span>•</span>
            <Link to={`/legal/${schoolCode}/refund-policy`} className="hover:text-primary hover:underline">
              Refund Policy
            </Link>
          </div>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Cloud Campus. Powered by{' '}
            <a 
              href="https://www.ncriptech.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              ncriptech
            </a>
          </p>
        </footer>
      </div>
    );
  }

  // Show login form for selected portal
  const Icon = selectedPortal.icon;
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-slate-900 p-4 relative">
      {/* Theme Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4"
        onClick={toggleTheme}
        data-testid="theme-toggle-login"
      >
        {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          {school?.logo_url ? (
            <img 
              src={school.logo_url} 
              alt={school.name}
              className="h-12 w-auto mx-auto mb-3"
            />
          ) : (
            <div className="bg-primary/10 p-3 rounded-full inline-block mb-3">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
          )}
          <h1 className="text-xl font-bold text-foreground">{school?.name}</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Code: <span className="font-mono">{schoolCode.toUpperCase()}</span>
          </p>
        </div>

        <Card className="border-2">
          <CardHeader className="text-center pb-2">
            <div className={`${selectedPortal.color} w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <CardTitle className="text-lg">{selectedPortal.title}</CardTitle>
            <p className="text-sm text-muted-foreground">Enter your credentials</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">Email / ID</Label>
                <Input 
                  id="email"
                  type="text" 
                  placeholder={`${selectedPortal.id}@${schoolCode.toLowerCase()}.edu`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input 
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className={`w-full ${selectedPortal.color} hover:opacity-90`}
                disabled={loginLoading}
              >
                {loginLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Logging in...</>
                ) : (
                  `Login as ${selectedPortal.title.replace(' Portal', '')}`
                )}
              </Button>
              
              {/* Install App Button - Show for all except Admin */}
              {selectedPortal.id !== 'admin' && (
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full mt-2"
                  onClick={handleInstallApp}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Install {school?.name} {selectedPortal.title.replace(' Portal', '')} App
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <button 
            onClick={() => setSelectedPortal(null)}
            className="text-sm text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4 inline mr-1" />
            Back to Portal Selection
          </button>
        </div>
      </motion.div>

      <footer className="mt-10 text-center space-y-3">
        <div className="flex justify-center gap-4 text-xs text-muted-foreground">
          <Link to={`/legal/${schoolCode}/privacy-policy`} className="hover:text-primary hover:underline">
            Privacy Policy
          </Link>
          <span>•</span>
          <Link to={`/legal/${schoolCode}/terms-of-use`} className="hover:text-primary hover:underline">
            Terms of Use
          </Link>
          <span>•</span>
          <Link to={`/legal/${schoolCode}/refund-policy`} className="hover:text-primary hover:underline">
            Refund Policy
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} Cloud Campus. Powered by{' '}
          <a 
            href="https://www.ncriptech.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline font-medium"
          >
            ncriptech
          </a>
        </p>
      </footer>

      {/* Install App Dialog */}
      <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Install {school?.name} {selectedPortal?.title?.replace(' Portal', '')} App
            </DialogTitle>
            <DialogDescription>
              Get quick access and offline support
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* QR Code */}
            {qrCode && (
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-lg border">
                  <img 
                    src={qrCode} 
                    alt="Install QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              </div>
            )}
            
            {/* Instructions */}
            <div className="space-y-3">
              {isIOS ? (
                <>
                  <p className="text-sm font-medium">iOS Installation:</p>
                  <ol className="text-sm space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">1</span>
                      <span>Tap the <strong>Share</strong> button at the bottom of Safari</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">2</span>
                      <span>Select <strong>"Add to Home Screen"</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">3</span>
                      <span>Tap <strong>"Add"</strong></span>
                    </li>
                  </ol>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">Android Installation:</p>
                  <ol className="text-sm space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">1</span>
                      <span>Tap the <strong>Menu (⋮)</strong> at top right of Chrome</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">2</span>
                      <span>Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0">3</span>
                      <span>Confirm installation</span>
                    </li>
                  </ol>
                </>
              )}
            </div>
            
            <div className="pt-2 border-t">
              <p className="text-xs text-center text-muted-foreground">
                <QrCode className="h-3 w-3 inline mr-1" />
                Scan QR code with another device to install
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantLoginPage;
